import { useRef } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Button } from "~/components/ui/button";
import { locales, type Locale } from "~/modules/lingui/lingui";

// Flag emoji mapping for each locale
const localeFlags: Record<Locale, string> = {
  en: "🇬🇧",
  sl: "🇸🇮",
};

// Short codes for display
const localeCodes: Record<Locale, string> = {
  en: "EN",
  sl: "SL",
};

interface LanguageSwitcherProps {
  currentLocale?: string;
  variant?: "header" | "sidebar";
  isMinimized?: boolean;
}

export function LanguageSwitcher({
  currentLocale = "en",
  variant = "header",
  isMinimized = false,
}: LanguageSwitcherProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const locale = currentLocale as Locale;

  const handleLanguageChange = (newLocale: string) => {
    if (!formRef.current) return;

    // Set the hidden input value and submit the form
    const input = formRef.current.querySelector('input[name="language"]') as HTMLInputElement;
    if (input) {
      input.value = newLocale;
      formRef.current.submit();
    }
  };

  // Hidden form that handles the actual submission
  const HiddenForm = () => (
    <form
      ref={formRef}
      method="POST"
      action="/api/update-language"
      style={{ display: "none" }}
    >
      <input type="hidden" name="language" value="" />
    </form>
  );

  // Minimized sidebar view - just flag
  if (variant === "sidebar" && isMinimized) {
    return (
      <div className="px-2 py-2 border-t">
        <HiddenForm />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-10 p-0 hover:bg-brand-dark-blue/5 flex items-center justify-center"
              title={locales[locale]}
            >
              <span className="text-lg">{localeFlags[locale]}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(Object.keys(locales) as Locale[]).map((lang) => (
              <DropdownMenuItem
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className="cursor-pointer"
              >
                <span className="mr-2">{localeFlags[lang]}</span>
                {locales[lang]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // Header view - compact: flag + code, dropdown shows flag + full name
  if (variant === "header") {
    return (
      <>
        <HiddenForm />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-3 text-sm border border-gray-200 bg-transparent hover:bg-gray-50 gap-1.5"
            >
              <span className="text-base">{localeFlags[locale]}</span>
              <span className="font-medium">{localeCodes[locale]}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(Object.keys(locales) as Locale[]).map((lang) => (
              <DropdownMenuItem
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className="cursor-pointer"
              >
                <span className="mr-2">{localeFlags[lang]}</span>
                {locales[lang]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }

  // Full sidebar view - compact inline style
  return (
    <>
      <HiddenForm />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 text-sm text-brand-dark-blue hover:text-brand-dark-blue/80 transition-colors">
            <span className="text-base">{localeFlags[locale]}</span>
            <span>{locales[locale]}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {(Object.keys(locales) as Locale[]).map((lang) => (
            <DropdownMenuItem
              key={lang}
              onClick={() => handleLanguageChange(lang)}
              className="cursor-pointer"
            >
              <span className="mr-2">{localeFlags[lang]}</span>
              {locales[lang]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
